'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

interface BreadcrumbItem {
  label: string;
  href: string;
}

interface BreadcrumbsProps {
  className?: string;
  customItems?: BreadcrumbItem[];
}

const Breadcrumbs = ({ className = '', customItems }: BreadcrumbsProps) => {
  const pathname = usePathname();

  const routeLabels: Record<string, string> = {
    '/homepage': 'Home',
    '/the-transformation-agenda': 'Transformation Agenda',
    '/investment-approach': 'Investment Approach',
    '/regional-operations': 'Regional Operations',
    '/impact-framework': 'Impact Framework',
    '/partnership-portal': 'Partnership Portal',
    '/leadership-team': 'Leadership Team',
  };

  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    if (customItems) {
      return customItems;
    }

    if (pathname === '/homepage' || pathname === '/') {
      return [];
    }

    const paths = pathname.split('/').filter(Boolean);
    const breadcrumbs: BreadcrumbItem[] = [
      { label: 'Home', href: '/homepage' },
    ];

    let currentPath = '';
    paths.forEach((path) => {
      currentPath += `/${path}`;
      const label = routeLabels[currentPath] || path.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
      breadcrumbs.push({
        label,
        href: currentPath,
      });
    });

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  if (breadcrumbs.length === 0) {
    return null;
  }

  return (
    <nav
      aria-label="Breadcrumb"
      className={`py-4 ${className}`}
    >
      <ol className="flex items-center space-x-2 text-sm">
        {breadcrumbs.map((item, index) => {
          const isLast = index === breadcrumbs.length - 1;

          return (
            <li key={item.href} className="flex items-center">
              {index > 0 && (
                <Icon
                  name="ChevronRightIcon"
                  size={16}
                  variant="outline"
                  className="mx-2 text-white/50"
                />
              )}
              {isLast ? (
                <span className="font-body font-medium text-white/50">
                  {item.label}
                </span>
              ) : (
                <Link
                  href={item.href}
                  className="font-body text-white/50 hover:text-white  transition-colors duration-300"
                >
                  {item.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;